import React, { useState, useCallback, useRef, useEffect } from 'react';
import { Button, Modal } from 'react-bootstrap';
import { ReactComponent as InfoIcon } from '../../images/info-circle.svg';
import { ReactComponent as VideoIcon } from '../../images/video_submission.svg';
import { ReactComponent as SuccessVideoIcon } from '../../images/success_video_upload_icon.svg';
import { useSelector } from 'react-redux';
import { RootState } from 'MyComponents/store';
import { isFileWithinSizeLimit } from 'Utils/fileValidation';
import { cameraErrorKey, requestCameraStream } from 'Utils/camera';
import { useTranslation } from 'react-i18next';

// Face verification records a selfie, so default to the front-facing camera on mobile.
const VIDEO_CONSTRAINTS: MediaStreamConstraints = {
  audio: false,
  video: {
    facingMode: "user",
    width: { ideal: 1280, max: 1280 },
    height: { ideal: 720, max: 720 },
    frameRate: { ideal: 24, max: 30 },
  },
};

type MyComponentProps = {
  videoConstraints?: any;
  show: boolean;
  handleClose: () => void;
  title: String;
  onVideoUpload?: any;
};

const VideoCaptureModal = (props: MyComponentProps) => {
  const { t } = useTranslation();
  const videoRef = useRef<HTMLVideoElement | any>(null);
  const mediaRecorderRef = useRef<MediaRecorder | any>(null);
  const [isRecording, setIsRecording] = useState(false);
  const [stream, setStream] = useState<MediaStream | any>(null);
  const [videoUrl, setVideoUrl] = useState<string | any>(null);
  const [videoBlob, setVideoBlob] = useState<string | any>(null);
  const [showDownloadProgressModal, setShowDownloadProgressModal] = useState(false);
  const [downloadProgress, setDownloadProgress] = useState(0);
  const [showDownloadProgressModalSuccessful, setShowDownloadProgressModalSuccessful] = useState(false);
  const [videoSizeInKB, setVideoSizeInKB] = useState(0);
  const [recordingTimeLeft, setRecordingTimeLeft] = useState(6);
  const [sizeErrorMsg, setSizeErrorMsg] = useState<string | null>(null);
  const timerRef = useRef<any>(null);
  const bitrateRef = useRef(800_000);
  const retryCountRef = useRef(0);
  const theme = useSelector((state: RootState) => state.theme.theme); // Access theme from Redux


  const videoName = 'video'; // Declare the videoName variable

  const stopCamera = useCallback(() => {
    setStream((currentStream: MediaStream | null) => {
      if (currentStream) {
        currentStream.getTracks().forEach((track: any) => track.stop());
      }
      return null;
    });
    if (videoRef.current) {
      videoRef.current.srcObject = null;
    }
  }, []);

  const startCamera = useCallback(() => {
    stopCamera();
    requestCameraStream(VIDEO_CONSTRAINTS)
      .then((newStream) => {
        if (videoRef.current) {
          videoRef.current.srcObject = newStream;
        }
        setStream(newStream);
        setSizeErrorMsg(null);
      })
      .catch((err) => {
        console.error("Error accessing camera:", err);
        setSizeErrorMsg(t(cameraErrorKey(err)));
      });
  }, [stopCamera, t]);

  useEffect(() => {
    if (props.show) {
      startCamera();
    } else {
      stopCamera();
    }
    return () => stopCamera();
  }, [props.show, startCamera, stopCamera]);


  const handleStartRecording = (activeStream?: MediaStream) => {
    const targetStream = activeStream || stream;
    if (!targetStream) return;
    setRecordingTimeLeft(5);
    const mimeType = MediaRecorder.isTypeSupported('video/webm;codecs=vp9')
      ? 'video/webm;codecs=vp9'
      : MediaRecorder.isTypeSupported('video/webm')
      ? 'video/webm'
      : '';
    const options: MediaRecorderOptions = { videoBitsPerSecond: bitrateRef.current };
    if (mimeType) options.mimeType = mimeType;
    mediaRecorderRef.current = new MediaRecorder(targetStream, options);
    mediaRecorderRef.current.addEventListener("dataavailable", handleDataAvailable);
    mediaRecorderRef.current.start();
    setIsRecording(true);

    // Auto-stop after 6 seconds
    timerRef.current = setInterval(() => {
      setRecordingTimeLeft((prev) => {
        if (prev <= 1) {
          handleStopRecording();
          return 0;
        }
        return prev - 1;
      });
    }, 1000);
  };

  const handleStopRecording = () => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== "inactive") {
      mediaRecorderRef.current.stop();
    }
    if (timerRef.current) {
      clearInterval(timerRef.current);
      timerRef.current = null;
    }
    setIsRecording(false);
    stopCamera();
  };

  const handleDataAvailable = async (e: BlobEvent) => {
    const mimeType = mediaRecorderRef.current?.mimeType || 'video/webm';
    const blob = new Blob([e.data], { type: mimeType });

    if (!isFileWithinSizeLimit(blob) && retryCountRef.current < 2) {
      // Silently re-record at half the bitrate — the user never sees this.
      retryCountRef.current += 1;
      bitrateRef.current = Math.max(200_000, Math.floor(bitrateRef.current / 2));
      try {
        const newStream = await requestCameraStream(VIDEO_CONSTRAINTS);
        if (videoRef.current) videoRef.current.srcObject = newStream;
        setStream(newStream);
        handleStartRecording(newStream);
      } catch (err) {
        console.error("Error accessing camera for size-retry recording:", err);
      }
      return;
    }

    if (!isFileWithinSizeLimit(blob)) {
      setSizeErrorMsg("Recorded video is too large. Please retake.");
    } else {
      setSizeErrorMsg(null);
    }

    const url = URL.createObjectURL(blob);
    setVideoUrl(url);
    setVideoBlob(blob);
  };

  const handleDoneClick = () => {
    const videoData = {
      videoName: 'video.mp4',
      videoSize: Math.floor(videoSizeInKB),
      videoUrl: videoUrl,
      videoBlob: videoBlob,
    };
    if (props.onVideoUpload) {
      props.onVideoUpload(videoData);
    }
    setShowDownloadProgressModalSuccessful(false);
  };

  const handleDownloadVideo = () => {
    if (videoUrl) {
      const a = document.createElement('a');
      a.style.display = 'none';
      document.body.appendChild(a);
      fetch(videoUrl)
        .then((response) => {
          const contentLength = response.headers.get('content-length');
          if (contentLength) {
            const videoSizeInBytes = parseInt(contentLength);
            const sizeInKB = videoSizeInBytes / 1024;
            setVideoSizeInKB(sizeInKB);
          }
          a.href = videoUrl;
          a.download = videoName;
          setTimeout(() => {
            a.click();
            document.body.removeChild(a);
          }, 500); // Reduced from 10 seconds to 0.5s
        })
        .catch((error) => console.error('Error fetching video metadata:', error));
    }
  };

  const resetVideo = () => {
    setVideoUrl(null);
    setVideoBlob(null);
    setSizeErrorMsg(null);
    retryCountRef.current = 0;
    bitrateRef.current = 800_000;
    startCamera();
  };

  const openDownloadProgressModal = () => {
    handleDownloadVideo();
    setShowDownloadProgressModal(true);

    const simulateDownload = async () => {
      for (let i = 0; i <= 100; i++) {
        await new Promise((resolve) => setTimeout(resolve, 15));
        setDownloadProgress(i);
      }
      setTimeout(() => {
        setShowDownloadProgressModal(false);
        setShowDownloadProgressModalSuccessful(true);
      }, 500);
    };
    simulateDownload();
    props.handleClose()

  };

  return (
    <>
      <Modal show={props.show} onHide={props.handleClose} centered fullscreen="sm-down">
        <Modal.Header closeButton className="custom-header">
          <Modal.Title className="fw-bold fs-5 letter-spacing-fix">
            Take a video
          </Modal.Title>
        </Modal.Header>
        <Modal.Body className={`${theme === "dark" && "bg-black"} bg-sky p-3`}>

          {/* VIDEO PREVIEW */}
          <div className="position-relative">

            {videoUrl ? (
              <video
                src={videoUrl}
                style={{ width: "100%" }}
                controls
                loop
                className="rounded border"
              />
            ) : (
              <video
                ref={videoRef}
                style={{ width: "100%" }}
                autoPlay
                muted
                playsInline
                className="rounded border"
              />
            )}

            {/* RECORDING INDICATOR */}
            {isRecording && (
              <div
                style={{
                  position: "absolute",
                  top: "10px",
                  right: "10px",
                  background: "rgba(255,0,0,0.85)",
                  color: "#fff",
                  padding: "4px 10px",
                  borderRadius: "20px",
                  fontSize: "0.8rem",
                  fontWeight: "600",
                }}
              >
                ● REC 00:0{recordingTimeLeft}
              </div>
            )}

          </div>


          {/* KYC INSTRUCTION CARD */}
          <div
            className="mt-3 p-3 rounded-3"
            style={{
              background: theme === "dark" ? "#1F1F1F" : "#ffffff",
              border: "1px solid #E4ECF7",
            }}
          >

            {/* Header */}
            <div className="d-flex align-items-center mb-2">
              <InfoIcon />
              <span className="fw-bold ms-2">KYC Video Verification</span>
            </div>

            {/* Intro */}
            <p className="small text-secondary mb-3">
              We will record a short video to confirm your identity.
            </p>

            {/* Action instruction */}
            {sizeErrorMsg ? (
              <div
                className="text-center p-2 rounded-2"
                style={{
                  background: theme === "dark" ? "#2A1515" : "#FDECEA",
                  border: "1px dashed #E53935",
                }}
              >
                <p className="small mb-0 text-danger fw-bold">{sizeErrorMsg}</p>
              </div>
            ) : (
              <div
                className="text-center p-2 rounded-2"
                style={{
                  background: theme === "dark" ? "#151515" : "#F0F6FF",
                  border: "1px dashed #C9DAF8",
                }}
              >
                <p className="small mb-0">
                  When ready, click <b>"Record Video"</b> to start recording. The recording will automatically stop after <b>5 seconds</b>.
                </p>
              </div>
            )}

          </div>


          <div className="d-flex flex-column flex-sm-row justify-content-center gap-2 mt-4">

            {!videoUrl ? (

              !isRecording ? (

                <Button
                  onClick={() => handleStartRecording()}
                  className="rounded-pill px-4 w-100"
                  style={{
                    background: "#0B1628",
                    borderColor: "#0B1628",
                  }}
                >
                  <i className="fa-solid fa-video me-2"></i>
                  Record Video
                </Button>

              ) : (

                <Button
                  onClick={handleStopRecording}
                  className="rounded-pill px-4 w-100"
                  style={{
                    background: "#E53935",
                    borderColor: "#E53935",
                  }}
                >
                  <i className="fa-solid fa-stop me-2"></i>
                  Stop Recording
                </Button>

              )

            ) : (

              <>
                <Button
                  onClick={resetVideo}
                  variant="outline-secondary"
                  className="rounded-pill w-100"
                >
                  <i className="fa-solid fa-rotate-left me-2"></i>
                  Retake
                </Button>

                {!sizeErrorMsg && (
                  <Button
                    onClick={openDownloadProgressModal}
                    className="rounded-pill w-100"
                    style={{
                      background: "#00D084",
                      borderColor: "#00D084",
                    }}
                  >
                    Submit Verification
                  </Button>
                )}
              </>

            )}

          </div>

        </Modal.Body>
      </Modal>

      <Modal show={showDownloadProgressModal} onHide={() => setShowDownloadProgressModal(false)} centered>
        <Modal.Header closeButton className="custom-header">
          <Modal.Title className="fw-bold fs-5 letter-spacing-fix">Video Submission</Modal.Title>
        </Modal.Header>
        <Modal.Body className={`${theme === "dark" && "bg-black"} bg-sky`}>
          <div className="text-center mt-2">
            <VideoIcon style={{ width: '50px', height: '50px' }} />
          </div>
          <div className="text-center">
            <progress className="w-75" value={downloadProgress} max="100" style={{ accentColor: '#00D084' }} />
            <p>{downloadProgress}%</p>
          </div>
        </Modal.Body>
      </Modal>

      <Modal show={showDownloadProgressModalSuccessful} onHide={() => setShowDownloadProgressModalSuccessful(false)} centered>
        <Modal.Header closeButton className="custom-header">
          <Modal.Title className="fw-bold fs-5 letter-spacing-fix">Status</Modal.Title>
        </Modal.Header>
        <Modal.Body className={`${theme === "dark" && "bg-black"} bg-sky`}>
          <div className="text-center mt-2">
            <SuccessVideoIcon style={{ width: '60px', height: '60px' }} />
          </div>
          <h5 className="text-center mt-3">Successfully Submitted</h5>
          <div className="text-center">
            <Button onClick={handleDoneClick} className="btn rounded-5 text-center" style={{ backgroundColor: "#0B1628", borderColor: "#0B1628", color: "#ffffff" }}>
              Done
            </Button>
          </div>
        </Modal.Body>
      </Modal>
    </>
  );
};

export default VideoCaptureModal;
